// src/pages/CategoriesPage.jsx
import React, { useState } from "react";
import { Btn, SectionHeader, InfoBanner, EmptyState, Badge } from "../components/UI";
import { addDocument, deleteDocument } from "../hooks/useFirestore";

export default function CategoriesPage({ categories, isAdmin }) {
  const [newCat, setNewCat] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  const handleAdd = async () => {
    const val = newCat.trim();
    if (!val) { setError("Category name cannot be empty."); return; }
    if (categories.find(c => c.name?.toLowerCase() === val.toLowerCase())) {
      setError("This category already exists."); return;
    }
    setError(""); setAdding(true);
    try {
      await addDocument("categories", { name: val });
      setNewCat("");
    } catch (err) {
      setError("Failed to add category.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Existing content won't be affected.`)) return;
    try {
      await deleteDocument("categories", cat.id);
    } catch (err) {
      alert("Delete failed.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAdd();
  };

  return (
    <div>
      {!isAdmin && <InfoBanner type="warning">👁 View-only mode.</InfoBanner>}

      <SectionHeader title="Categories" />

      <p style={{ fontSize: 14, color: "#666", marginBottom: 20, lineHeight: 1.6 }}>
        Categories are used across Blogs, Magazines, and AgriKnowledge videos to help
        readers find relevant content. Deleting a category won't remove it from existing content.
      </p>

      {/* Add new category (admin only) */}
      {isAdmin && (
        <div style={{
          background: "#fff", border: "1px solid #e8f0e9", borderRadius: 12,
          padding: "18px 20px", marginBottom: 24,
        }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 8 }}>
            Add New Category
          </label>
          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6,
              padding: "8px 12px", marginBottom: 10, fontSize: 13, color: "#c53030" }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: 10, maxWidth: 420 }}>
            <input
              value={newCat}
              onChange={e => setNewCat(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. Drip Irrigation"
              style={{
                flex: 1, padding: "9px 12px", border: "1px solid #ddd", borderRadius: 8,
                fontFamily: "inherit", fontSize: 14, background: "#fafaf8", outline: "none",
              }}
            />
            <Btn onClick={handleAdd} disabled={adding || !newCat.trim()}>
              {adding ? "Adding..." : "+ Add"}
            </Btn>
          </div>
          <p style={{ fontSize: 11, color: "#aaa", margin: "8px 0 0" }}>Press Enter or click Add to create the category.</p>
        </div>
      )}

      {/* Category list */}
      {categories.length ? (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#555", marginBottom: 12 }}>
            {categories.length} {categories.length === 1 ? "Category" : "Categories"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {categories.map(cat => (
              <div key={cat.id} style={{
                background: "#fff", border: "1px solid #d4ebd6", borderRadius: 10,
                padding: "9px 14px", display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ fontSize: 16 }}>🏷️</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>{cat.name}</span>
                {isAdmin && (
                  <button
                    onClick={() => handleDelete(cat)}
                    style={{
                      background: "#fee2e2", border: "none", borderRadius: 4, width: 22, height: 22,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", color: "#c53030", fontSize: 12, fontWeight: 700,
                    }}
                    title={`Delete ${cat.name}`}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <EmptyState
          icon="🏷️"
          message={isAdmin ? "No categories yet. Add your first category above." : "No categories available."}
        />
      )}
    </div>
  );
}
